/**
 * Veľký panel vybraného hrdinu (docs/design/4-vyber-bojovnikov.png): art,
 * meno, základné čísla ako segmentové ukazovatele a pomocníci. Používa ho
 * výber pri zakladaní stola aj predsieň online zápasu.
 */

import type { CardDef, HeroDef } from '@unmatched/engine';
import { content, localizeHero } from '@unmatched/content';
import { IMPLEMENTED_HEROES } from '@unmatched/engine';
import type React from 'react';

import { artOf } from '../screens/Home.js';

interface Props {
  hero: HeroDef | null;
  slot: number;
  /** Návestie miesta, napr. „1P · Tyrian" alebo „Hráč 2 · tím B". */
  seat: string;
  status?: React.ReactNode;
  active?: boolean;
  compact?: boolean;
  onClick?: () => void;
  onShowDeck?: (hero: HeroDef) => void;
}

/** Priemerná útočná hodnota balíčka — univerzál sa počíta do útoku, ako v pravidlách. */
export function averageAttack(hero: HeroDef): number {
  const cards = (content.decks[hero.deckId]?.cards ?? []).filter(
    (card: CardDef) => card.type === 'attack' || card.type === 'versatile',
  );
  const count = cards.reduce((sum, card) => sum + card.qty, 0);
  return count ? cards.reduce((sum, card) => sum + card.value * card.qty, 0) / count : 0;
}

function Pips({ value, max = 5, tone }: { value: number; max?: number; tone: string }) {
  return (
    <span className={`pips5 pips5--${tone}`} aria-hidden="true">
      {Array.from({ length: max }, (_, i) => (
        <span key={i} className={i < value ? 'is-on' : ''} />
      ))}
    </span>
  );
}

export function HeroPanel({
  hero,
  slot,
  seat,
  status,
  active,
  compact,
  onClick,
  onShowDeck,
}: Props) {
  // Klikateľná je len hlavička s artom — tlačidlo Balíček nesmie byť
  // tlačidlom v tlačidle (neplatné HTML).
  const Select = onClick ? 'button' : 'span';
  const className = [
    'heroPanel',
    `heroPanel--p${slot}`,
    active ? 'is-active' : '',
    compact ? 'heroPanel--compact' : '',
    hero ? '' : 'is-empty',
  ]
    .filter(Boolean)
    .join(' ');

  const art = artOf(hero?.id);
  const special = hero ? localizeHero(hero, 'en').special : null;
  const ready = hero ? IMPLEMENTED_HEROES.includes(hero.id) : false;
  const attack = hero ? averageAttack(hero) : 0;

  return (
    <div className={className}>
      <Select type={onClick ? 'button' : undefined} className="heroPanel__select" onClick={onClick}>
        <span className="heroPanel__top">
          <span className="seatTag">{seat}</span>
          {status ? <span className="heroPanel__status">{status}</span> : null}
        </span>

        <span className="heroPanel__art">
          {art ? <img src={art} alt="" draggable={false} /> : null}
          {hero ? (
            <span className="heroPanel__caption">
              <span className="heroPanel__kind">
                {hero.attack === 'melee' ? 'Zblízka' : 'Na diaľku'}
                {hero.set ? ` · ${hero.set}` : ''}
              </span>
              <span className="heroPanel__name">{hero.name}</span>
            </span>
          ) : (
            <span className="heroPanel__none">Vyber postavu zo súpisky</span>
          )}
          {hero ? <span className="heroPanel__hp">♥ {hero.hp} HP</span> : null}
        </span>
      </Select>

      {hero && !compact ? (
        <span className="heroPanel__stats">
          <span className="statRow">
            <span>Sila útoku</span>
            <Pips value={Math.round(attack)} tone="primary" />
            <b>{attack.toFixed(1)}</b>
          </span>
          <span className="statRow">
            <span>Pohyb</span>
            <Pips value={hero.move} tone="blue" />
            <b>{hero.move}</b>
          </span>
          <span className="statRow">
            <span>Zdravie</span>
            <Pips value={Math.round(hero.hp / 4)} tone="red" />
            <b>{hero.hp}</b>
          </span>
          <span className="statRow statRow--text">
            <span>Pomocníci</span>
            <b>
              {hero.sidekicks?.length
                ? hero.sidekicks.map((s) => `${s.count}× ${s.name} (${s.hp} HP)`).join(', ')
                : 'žiadni'}
            </b>
          </span>
        </span>
      ) : null}

      {hero && !compact && special ? (
        <span className="heroPanel__special">
          <span className="eyebrow">Schopnosť</span>
          <span className="heroPanel__specialText">{special}</span>
        </span>
      ) : null}

      {hero ? (
        <span className="heroPanel__foot">
          {ready ? (
            <span className="tag tag--ok">Efekty hotové</span>
          ) : (
            <span className="tag tag--wip">WIP · nehrateľná</span>
          )}
          {onShowDeck ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => onShowDeck(hero)}
            >
              Balíček · 30 kariet
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/** Miesto, ktorého voľba je skrytá (online súper si vyberá sám). */
export function SecretPanel({
  slot,
  seat,
  title,
  children,
}: {
  slot: number;
  seat: string;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={`heroPanel heroPanel--p${slot} heroPanel--secret`}>
      <span className="heroPanel__top">
        <span className="seatTag">{seat}</span>
      </span>
      <span className="secret">
        <span className="secret__icon" aria-hidden="true">
          ?
        </span>
        <span className="secret__title">{title}</span>
        {children}
      </span>
    </div>
  );
}
